import {memo,useLayoutEffect,useRef} from 'react';
import type {Snapshot} from '@platform/contracts/x';
import {drawNumber} from '@platform/contracts/drawNumber';
import {DrawReference} from '@platform/ui/DrawReference';
import {useCopy} from '@platform/ui/config';
import {Ball} from '@platform/ui/Ball';
import {useConfirmedBallAnimation} from '@platform/ui/ConfirmedBallMotion';
import {useWebViewport} from '@platform/ui/config';
import {CardSectionHeading} from './CardSectionHeading';
import './DrawNumberBoard.css';

// Read-only projection of the already confirmed draw. No separate feed or timer.
export function StageDrawNumberBoard({state}:{state:Snapshot}){
 const desktop=useWebViewport();
 return desktop?<DrawNumberBoard state={state} placement="stage"/>:null;
}

export function DrawNumberBoard({state,placement='dialog'}:{state:Snapshot;placement?:'stage'|'dialog'}){
 const t=useCopy();
 const frame=useRef<HTMLDivElement>(null);
 useLayoutEffect(()=>{if(frame.current)frame.current.scrollTop=0;},[state.drawId]);
 useLayoutEffect(()=>{
  const element=frame.current;if(placement!=='stage'||!element)return;
  const dialog=element.closest('dialog'),source=dialog??window;
  const position=()=>dialog?.scrollTop??window.scrollY;
  let previous=position(),manual=false,tick=0;
  // Follow page movement by its delta, never by an absolute card-list offset.
  // Direct table input takes ownership for this draw, so it cannot snap back.
  const follow=()=>{tick=0;const next=position(),delta=next-previous;previous=next;if(!manual&&element.clientHeight)element.scrollTop+=delta;};
  const scroll=()=>{if(!tick)tick=requestAnimationFrame(follow);};
  const takeOver=()=>{manual=true;};
  const key=(event:KeyboardEvent)=>{if(['ArrowUp','ArrowDown','PageUp','PageDown','Home','End',' '].includes(event.key))takeOver();};
  source.addEventListener('scroll',scroll,{passive:true});
  for(const event of ['wheel','pointerdown','touchstart'])element.addEventListener(event,takeOver,{passive:true});
  element.addEventListener('keydown',key);
  return()=>{cancelAnimationFrame(tick);source.removeEventListener('scroll',scroll);for(const event of ['wheel','pointerdown','touchstart'])element.removeEventListener(event,takeOver);element.removeEventListener('keydown',key);};
 },[placement,state.drawId]);
 const called=new Set(state.balls.map(b=>b.number)),last=state.balls.at(-1);
 return <section className="draw-number-board" data-placement={placement} data-draw-id={state.drawId} aria-label={t('room.allNumbers')}>
  {placement==='stage'?<CardSectionHeading label={t('desktop.boardTitle')}/>:<header><span>{t('room.ballCount',{count:state.balls.length})}</span><DrawReference draw={drawNumber(state)}/></header>}
  <div ref={frame} className="draw-number-board-frame" tabIndex={placement==='stage'?0:undefined} role={placement==='stage'?'region':undefined} aria-label={placement==='stage'?t('desktop.boardTitle'):undefined}><div className="draw-number-board-grid">{Array.from({length:90},(_,i)=>i+1).map(number=><DrawBoardBall key={number} number={number} marked={called.has(number)} latest={last?.number===number} drawId={state.drawId} groups={state.ballColors}/>)}</div></div>
 </section>;
}

const DrawBoardBall=memo(function DrawBoardBall({number,marked,latest,drawId,groups}:{number:number;marked:boolean;latest:boolean;drawId:string;groups:Snapshot['ballColors']}){
 const t=useCopy(),label=t(latest?'desktop.numberLast':marked?'desktop.numberCalled':'desktop.numberWaiting',{number});
 const ref=useConfirmedBallAnimation<HTMLSpanElement>(marked&&latest?drawId:undefined,number,'board');
 return <span ref={ref} className={'draw-board-number '+(marked?'marked':'unmarked')+(latest?' latest-mark':'')} data-number={number} role="img" aria-label={label} title={label}><Ball number={number} groups={groups} className="room-small-ball" uncalled={!marked}/></span>;
});
